import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  api,
  type PendingQuote, type QuoteDetail, type QuoteAddress,
  type PendingOrder, type OrderDetail,
  type PendingInvoice, type InvoiceDetail,
  type DealRow, type DealDetail,
} from '../api';
import Stepper from '../components/Stepper';
import { InvoiceStatusBadge, DeliveryStatusBadge, InvoiceBadge } from '../components/StatusBadge';

// ---------- helpers partagés ----------
function fmtMoney(n: number, currency: string | null): string {
  return `${n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}
function fmtDate(d: string | false): string {
  if (!d) return '—';
  const day = String(d).slice(0, 10);
  const parts = day.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : day;
}

type Toast = { kind: 'ok' | 'err'; text: string } | null;

function AddressLines({ addr }: { addr: QuoteAddress | null }) {
  if (!addr || addr.lines.length === 0) return <span className="devis-addr-empty">Non renseignée</span>;
  return (
    <div className="devis-addr-body">
      <strong>{addr.name}</strong>
      {addr.lines.map((l, i) => <span key={i}>{l}</span>)}
    </div>
  );
}

function AddressBlock({ detail }: { detail: QuoteDetail | OrderDetail }) {
  if (!detail.invoice_address && !detail.shipping_address) return null;
  return (
    <div className="devis-addresses">
      {detail.same_address ? (
        <div className="devis-addr">
          <span className="devis-addr-title">Facturation &amp; livraison</span>
          <AddressLines addr={detail.invoice_address ?? detail.shipping_address} />
        </div>
      ) : (
        <>
          <div className="devis-addr">
            <span className="devis-addr-title">Facturation</span>
            <AddressLines addr={detail.invoice_address} />
          </div>
          <div className="devis-addr devis-addr-ship">
            <span className="devis-addr-title">Livraison</span>
            <AddressLines addr={detail.shipping_address} />
          </div>
        </>
      )}
    </div>
  );
}

function LinesTable({ lines }: { lines: QuoteDetail['lines'] }) {
  return (
    <table className="devis-lines">
      <thead>
        <tr>
          <th>Article</th>
          <th className="num">Qté</th>
          <th className="num">P.U.</th>
          <th className="num">Sous-total</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td>{l.name}</td>
            <td className="num">{l.qty}</td>
            <td className="num">{fmtMoney(l.price_unit, null)}</td>
            <td className="num">{fmtMoney(l.subtotal, null)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ ut, tax, total, currency }: { ut: number; tax: number; total: number; currency: string | null }) {
  return (
    <div className="devis-totals">
      <div><span>Hors taxe</span><strong>{fmtMoney(ut, currency)}</strong></div>
      <div><span>Taxes</span><strong>{fmtMoney(tax, currency)}</strong></div>
      <div className="total"><span>Total</span><strong>{fmtMoney(total, currency)}</strong></div>
    </div>
  );
}

// Ouvre (ou reprend) la conversation liée à un document Odoo et bascule sur le chat.
function useAskAssistant(setToast: (t: Toast) => void) {
  const nav = useNavigate();
  return async (docKey: string, title: string, prefix: string) => {
    try {
      const { conversation, existing } = await api.openDocConversation(docKey, title);
      nav('/chat', { state: existing ? { conv: conversation.id } : { conv: conversation.id, draft: prefix } });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    }
  };
}

// ---------- Onglet Devis ----------
function DevisTab() {
  const [items, setItems] = useState<PendingQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<QuoteDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  function reload() {
    setLoading(true);
    setError(null);
    return api.listPendingQuotes().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getQuote(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);

  function askForQuote() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur le devis Odoo **${detail.name}** (sale.order id ${detail.id}), en attente de validation.\n` +
      `- Client : ${detail.partner ?? '—'}${detail.client_order_ref ? ` (réf. client ${detail.client_order_ref})` : ''}\n` +
      `- Total actuel : ${fmtMoney(detail.amount_total, detail.currency)} (HT ${fmtMoney(detail.amount_untaxed, detail.currency)}, taxes ${fmtMoney(detail.amount_tax, detail.currency)})\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`sale.order:${detail.id}`, `Devis ${detail.name}`, prefix);
  }

  async function validate() {
    if (selectedId == null || validating) return;
    if (!confirm('Confirmer ce devis en bon de commande et envoyer la confirmation au client ?')) return;
    setValidating(true);
    setToast(null);
    try {
      const res = await api.validateQuote(selectedId);
      setItems((cur) => cur.filter((q) => q.id !== selectedId));
      setSelectedId(null);
      setToast({ kind: 'ok', text: res.warning ? `${res.name} confirmé. ⚠️ ${res.warning}` : `${res.name} confirmé et envoyé au client. ✅ Retrouvez-le dans l'onglet Commandes.` });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setValidating(false);
    }
  }

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <p className="devis-sub">Devis créés automatiquement, en attente de validation.</p>
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">🎉 Aucun devis en attente de validation.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr><th>N°</th><th>Client</th><th className="num">Montant</th><th>Date</th><th>Échéance</th></tr></thead>
              <tbody>
                {items.map((q) => (
                  <tr key={q.id} className={q.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(q.id)}>
                    <td className="mono">{q.name}</td>
                    <td>{q.partner ?? '—'}</td>
                    <td className="num">{fmtMoney(q.amount_total, q.currency)}</td>
                    <td>{fmtDate(q.date_order)}</td>
                    <td>{fmtDate(q.activity_deadline)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedId != null && (
          <aside className="devis-detail">
            <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
            {detailLoading && <div className="devis-empty">Chargement du devis…</div>}
            {detail && (
              <>
                <h2>{detail.name}</h2>
                <Stepper stage={0} />
                <div className="devis-meta">
                  <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
                  <div><span>Email</span>{detail.partner_email ? <strong>{detail.partner_email}</strong> : <strong className="warn">aucun email</strong>}</div>
                  <div><span>Date</span><strong>{fmtDate(detail.date_order)}</strong></div>
                  {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
                </div>
                <AddressBlock detail={detail} />
                <LinesTable lines={detail.lines} />
                <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
                {!detail.partner_email && <div className="devis-warn">⚠️ Ce client n'a pas d'adresse email : le devis sera confirmé mais aucun email ne partira.</div>}
                <div className="devis-actions">
                  <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
                  <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
                  <button className="devis-ask" onClick={askForQuote}>💬 Demander une modification</button>
                  <button className="devis-validate" onClick={validate} disabled={validating}>
                    {validating ? 'Validation…' : 'Valider & envoyer au client'}
                  </button>
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </>
  );
}

// ---------- Onglet Commandes ----------
function CommandesTab() {
  const [items, setItems] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [deliverQty, setDeliverQty] = useState<Record<number, number>>({});
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  function reload() {
    setLoading(true);
    setError(null);
    return api.listPendingOrders().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getOrder(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);
  // Quantités à livrer par défaut = stock disponible.
  useEffect(() => {
    if (!detail) { setDeliverQty({}); return; }
    const init: Record<number, number> = {};
    for (const dl of detail.deliver_lines) init[dl.move_id] = dl.available;
    setDeliverQty(init);
  }, [detail]);

  function askForOrder() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur la commande Odoo **${detail.name}** (sale.order id ${detail.id}), confirmée, en attente de livraison/facturation.\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`sale.order:${detail.id}`, `Commande ${detail.name}`, prefix);
  }

  async function validate() {
    if (selectedId == null || validating || !detail) return;
    const deliveries = detail.deliver_lines.length
      ? detail.deliver_lines.map((dl) => ({ move_id: dl.move_id, qty: deliverQty[dl.move_id] ?? dl.available }))
      : undefined;
    const total = deliveries ? deliveries.reduce((s, d) => s + (d.qty || 0), 0) : 1;
    if (total <= 0) { setToast({ kind: 'err', text: 'Aucune quantité à livrer.' }); return; }
    if (!confirm('Valider la commande ? Les quantités saisies seront livrées (sortie de stock), le reste passera en reliquat (backorder), puis la facture du livré sera créée.')) return;
    setValidating(true);
    setToast(null);
    try {
      const res = await api.validateOrder(selectedId, deliveries);
      setItems((cur) => cur.filter((o) => o.id !== selectedId));
      setSelectedId(null);
      const bo = res.backorder ? ' (reliquat créé pour le reste)' : '';
      const invLabel = res.invoice_name ? ` Facture ${res.invoice_name} créée` : ' Facture créée';
      setToast({ kind: 'ok', text: `${res.name} livrée${bo}.${invLabel} — à valider dans l'onglet Factures. ✅` });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setValidating(false);
    }
  }

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <p className="devis-sub">Bons de commande confirmés, à livrer et facturer.</p>
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">🎉 Aucune commande en attente.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr><th>N°</th><th>Client</th><th className="num">Montant</th><th>Date</th><th>Livraison</th><th>Facturation</th></tr></thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className={o.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(o.id)}>
                    <td className="mono">{o.name}</td>
                    <td>{o.partner ?? '—'}</td>
                    <td className="num">{fmtMoney(o.amount_total, o.currency)}</td>
                    <td>{fmtDate(o.date_order)}</td>
                    <td><DeliveryStatusBadge status={o.delivery_status} /></td>
                    <td><InvoiceStatusBadge status={o.invoice_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedId != null && (
          <aside className="devis-detail">
            <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
            {detailLoading && <div className="devis-empty">Chargement de la commande…</div>}
            {detail && (
              <>
                <h2>{detail.name}</h2>
                <Stepper stage={detail.stage} />
                <div className="devis-meta">
                  <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
                  <div><span>Livraison</span><strong><DeliveryStatusBadge status={detail.delivery_status} /></strong></div>
                  <div><span>Facturation</span><strong><InvoiceStatusBadge status={detail.invoice_status} /></strong></div>
                  <div><span>Date</span><strong>{fmtDate(detail.date_order)}</strong></div>
                  {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
                </div>
                <AddressBlock detail={detail} />
                {detail.deliver_lines.length > 0 ? (
                  <>
                    <p className="deliver-hint">Choisissez les quantités à livrer (le reste part en reliquat) :</p>
                    <table className="devis-lines">
                      <thead><tr><th>Article</th><th className="num">Cmd</th><th className="num">Dispo</th><th className="num">À livrer</th></tr></thead>
                      <tbody>
                        {detail.deliver_lines.map((dl) => (
                          <tr key={dl.move_id}>
                            <td>{dl.product ?? '—'}</td>
                            <td className="num">{dl.ordered}</td>
                            <td className={`num ${dl.available < dl.ordered ? 'stock-short' : ''}`}>{dl.available}</td>
                            <td className="num">
                              <input className="deliver-input" type="number" min={0} max={dl.ordered}
                                value={deliverQty[dl.move_id] ?? dl.available}
                                onChange={(e) => setDeliverQty((q) => ({ ...q, [dl.move_id]: Math.max(0, Math.min(Number(e.target.value) || 0, dl.ordered)) }))} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <LinesTable lines={detail.lines} />
                )}
                <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
                <div className="devis-actions">
                  <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
                  {(detail.delivery_status === 'full' || detail.delivery_status === 'partial') && (
                    <a className="devis-odoo" href={`/api/orders/${detail.id}/delivery-pdf`}>⬇ Bon de livraison (PDF)</a>
                  )}
                  <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
                  <button className="devis-ask" onClick={askForOrder}>💬 Demander une modification</button>
                  <button className="devis-validate" onClick={validate} disabled={validating}>
                    {validating ? 'Traitement…' : 'Valider la commande (livrer + facturer)'}
                  </button>
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </>
  );
}

// ---------- Onglet Factures ----------
function FacturesTab() {
  const [items, setItems] = useState<PendingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<null | 'post' | 'send' | 'pay'>(null);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  function reload() {
    setLoading(true);
    setError(null);
    return api.listInvoices().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);

  function loadDetail(id: number) {
    setDetailLoading(true);
    api.getInvoice(id).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetail(null);
    loadDetail(selectedId);
  }, [selectedId]);

  function askForInvoice() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur la facture Odoo **${detail.name}** (account.move id ${detail.id}).\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)} (restant dû ${fmtMoney(detail.amount_residual, detail.currency)})\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`account.move:${detail.id}`, `Facture ${detail.name}`, prefix);
  }

  async function run(action: 'post' | 'send' | 'pay', confirmMsg: string, okMsg: (r: any) => string) {
    if (selectedId == null || busy) return;
    if (!confirm(confirmMsg)) return;
    setBusy(action);
    setToast(null);
    try {
      const fn = action === 'post' ? api.postInvoice : action === 'send' ? api.sendInvoice : api.payInvoice;
      const res = await fn(selectedId);
      setToast({ kind: 'ok', text: okMsg(res) });
      loadDetail(selectedId);   // rafraîchir l'état affiché
      reload();                 // et la liste (badges)
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <p className="devis-sub">Factures clients : brouillons à valider puis suivi des paiements.</p>
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">Aucune facture.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr><th>N°</th><th>Client</th><th className="num">Montant</th><th>Date</th><th>État</th></tr></thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id} className={f.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(f.id)}>
                    <td className="mono">{f.name}</td>
                    <td>{f.partner ?? '—'}</td>
                    <td className="num">{fmtMoney(f.amount_total, f.currency)}</td>
                    <td>{fmtDate(f.invoice_date)}</td>
                    <td><InvoiceBadge state={f.state} payment={f.payment_state} overdue={f.overdue} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedId != null && (
          <aside className="devis-detail">
            <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
            {detailLoading && <div className="devis-empty">Chargement de la facture…</div>}
            {detail && (
              <>
                <h2>{detail.name}</h2>
                <Stepper stage={detail.stage} />
                <div className="devis-meta">
                  <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
                  <div><span>État</span><strong><InvoiceBadge state={detail.state} payment={detail.payment_state} overdue={detail.overdue} /></strong></div>
                  {detail.invoice_origin && <div><span>Origine</span><strong>{detail.invoice_origin}</strong></div>}
                  {detail.client_ref && <div><span>Réf. client</span><strong>{detail.client_ref}</strong></div>}
                  <div><span>Date</span><strong>{fmtDate(detail.invoice_date)}</strong></div>
                  <div><span>Échéance</span><strong>{fmtDate(detail.invoice_date_due)}</strong></div>
                  {detail.state === 'posted' && detail.payment_state !== 'paid' && (
                    <div><span>Restant dû</span><strong>{fmtMoney(detail.amount_residual, detail.currency)}</strong></div>
                  )}
                </div>
                <LinesTable lines={detail.lines} />
                <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
                {!detail.partner_email && detail.state === 'posted' && (
                  <div className="devis-warn">⚠️ Ce client n'a pas d'adresse email : l'envoi de la facture ne sera pas possible.</div>
                )}
                <div className="devis-actions">
                  <a className="devis-odoo" href={`/api/invoices/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
                  <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
                  <button className="devis-ask" onClick={askForInvoice}>💬 Demander une modification</button>
                  {detail.state === 'draft' && (
                    <button className="devis-validate" disabled={busy != null}
                      onClick={() => run('post', 'Valider (comptabiliser) cette facture ?', (r) => `Facture ${r.name ?? ''} validée. ✅`)}>
                      {busy === 'post' ? 'Validation…' : 'Valider la facture'}
                    </button>
                  )}
                  {detail.state === 'posted' && detail.payment_state !== 'paid' && detail.payment_state !== 'in_payment' && (
                    <>
                      <button className="devis-ask" disabled={busy != null || !detail.partner_email}
                        onClick={() => run('send', 'Envoyer la facture PDF au client par email ?', () => 'Facture envoyée au client. ✅')}>
                        {busy === 'send' ? 'Envoi…' : '✉️ Envoyer au client'}
                      </button>
                      <button className="devis-validate" disabled={busy != null}
                        onClick={() => run('pay', 'Enregistrer le paiement de cette facture (marquer comme payée) ?', () => 'Paiement enregistré. ✅')}>
                        {busy === 'pay' ? 'Enregistrement…' : 'Marquer comme payée'}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </>
  );
}

// ---------- Onglet Suivi (workflow unifié par vente) ----------
type DealFilter = 'en_cours' | 'tout' | 'terminees';

function SuiviTab() {
  const [items, setItems] = useState<DealRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DealFilter>('en_cours');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DealDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  function reload() {
    setLoading(true);
    setError(null);
    return api.getPipeline().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);

  function loadDeal(id: number) {
    setDetailLoading(true);
    api.getDeal(id).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetail(null);
    loadDeal(selectedId);
  }, [selectedId]);

  const visible = items.filter((d) =>
    filter === 'tout' ? true : filter === 'terminees' ? d.stage >= 5 : d.stage < 5
  );

  function askForDeal() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const kindLabel = detail.doc_key.startsWith('account.move') ? 'la facture' : detail.order_state === 'sale' ? 'la commande' : 'le devis';
    const prefix =
      `Je travaille sur ${kindLabel} Odoo **${detail.name}** (${detail.doc_key}).\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(detail.doc_key, `${detail.name}`, prefix);
  }

  // Exécute la prochaine étape en appelant l'endpoint correspondant au type d'action.
  async function doAction(action: string, confirmMsg: string, call: () => Promise<any>, ok: (r: any) => string) {
    if (!detail || busy) return;
    if (!confirm(confirmMsg)) return;
    setBusy(action);
    setToast(null);
    try {
      const res = await call();
      setToast({ kind: 'ok', text: ok(res) });
      loadDeal(detail.id);
      reload();
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setBusy(null);
    }
  }

  function NextStep({ d }: { d: DealDetail }) {
    const inv = d.invoice;
    switch (d.next.kind) {
      case 'validate_quote':
        return (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('q', 'Confirmer ce devis en commande et envoyer la confirmation au client ?',
              () => api.validateQuote(d.id), (r) => r.warning ? `${r.name} confirmé. ⚠️ ${r.warning}` : `${r.name} confirmé et envoyé. ✅`)}>
            {busy ? 'Traitement…' : `▶ ${d.next.label}`}
          </button>
        );
      case 'validate_order':
        return (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('o', 'Valider la commande ? Livraison (sortie de stock) puis création de la facture à valider.',
              () => api.validateOrder(d.id), (r) => `${r.name} livrée${r.backorder ? ' (reliquat créé)' : ''}. ${r.invoice_name ? `Facture ${r.invoice_name}` : 'Facture'} créée. ✅`)}>
            {busy ? 'Traitement…' : `▶ ${d.next.label}`}
          </button>
        );
      case 'post_invoice':
        return inv ? (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('p', 'Valider (comptabiliser) la facture ?',
              () => api.postInvoice(inv.id), (r) => `Facture ${r.name ?? ''} validée. ✅`)}>
            {busy ? 'Traitement…' : `▶ ${d.next.label}`}
          </button>
        ) : null;
      case 'invoice_actions':
        return inv ? (
          <div className="next-multi">
            <button className="devis-ask" disabled={busy != null || !d.partner_email}
              onClick={() => doAction('s', 'Envoyer la facture PDF au client par email ?',
                () => api.sendInvoice(inv.id), () => 'Facture envoyée au client. ✅')}>
              {busy === 's' ? 'Envoi…' : '✉️ Envoyer au client'}
            </button>
            <button className="next-btn" disabled={busy != null}
              onClick={() => doAction('pay', 'Enregistrer le paiement de cette facture (marquer payée) ?',
                () => api.payInvoice(inv.id), () => 'Paiement enregistré. ✅')}>
              {busy === 'pay' ? 'Enregistrement…' : '▶ Marquer comme payée'}
            </button>
          </div>
        ) : null;
      default:
        return <div className="next-done">✓ Vente terminée — rien à faire.</div>;
    }
  }

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <div className="suivi-filters">
          {([['en_cours', 'En cours'], ['tout', 'Tout'], ['terminees', 'Terminées']] as [DealFilter, string][]).map(([k, lbl]) => (
            <button key={k} className={`suivi-filter ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{lbl}</button>
          ))}
        </div>
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && visible.length === 0 && <div className="devis-empty">Aucune vente à afficher.</div>}
          {!loading && !error && visible.length > 0 && (
            <table>
              <thead><tr><th>N°</th><th>Client</th><th className="num">Montant</th><th>Avancement</th><th>Prochaine étape</th></tr></thead>
              <tbody>
                {visible.map((d) => (
                  <tr key={d.id} className={d.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(d.id)}>
                    <td className="mono">{d.name}</td>
                    <td>{d.partner ?? '—'}</td>
                    <td className="num">{fmtMoney(d.amount_total, d.currency)}</td>
                    <td><Stepper stage={d.stage} compact /></td>
                    <td className="suivi-next">{d.stage >= 5 ? '✓ Terminé' : d.next_label}{(d.late || d.overdue) && <span className="suivi-overdue"> · ⏰ en retard</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedId != null && (
          <aside className="devis-detail">
            <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
            {detailLoading && <div className="devis-empty">Chargement…</div>}
            {detail && (
              <>
                <h2>{detail.name}</h2>
                <Stepper stage={detail.stage} />
                <div className="devis-meta">
                  <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
                  <div><span>Livraison</span><strong><DeliveryStatusBadge status={detail.delivery_status} /></strong></div>
                  {detail.expected_date && detail.stage < 3 && (
                    <div><span>Livraison prévue</span><strong>{fmtDate(detail.expected_date)}{detail.late && <span className="status-badge tone-danger" style={{ marginLeft: 6 }}>En retard</span>}</strong></div>
                  )}
                  <div><span>Facturation</span><strong><InvoiceStatusBadge status={detail.invoice_status} /></strong></div>
                  {detail.invoice && (
                    <div><span>Facture</span><strong>{detail.invoice.name} <InvoiceBadge state={detail.invoice.state} payment={detail.invoice.payment_state} overdue={detail.invoice.overdue} /></strong></div>
                  )}
                  {detail.invoice && detail.invoice.state === 'posted' && detail.invoice.payment_state !== 'paid' && (
                    <div><span>Restant dû</span><strong>{fmtMoney(detail.invoice.amount_residual, detail.currency)}</strong></div>
                  )}
                  <div><span>Date</span><strong>{fmtDate(detail.date_order)}</strong></div>
                  {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
                </div>
                <AddressBlock detail={detail as unknown as OrderDetail} />
                {detail.stage < 3 ? (
                  <table className="devis-lines">
                    <thead>
                      <tr><th>Article</th><th className="num">Qté</th><th className="num">Dispo</th><th className="num">P.U.</th><th className="num">Sous-total</th></tr>
                    </thead>
                    <tbody>
                      {detail.lines.map((l) => (
                        <tr key={l.id}>
                          <td>{l.name}</td>
                          <td className="num">{l.qty}</td>
                          <td className={`num ${l.shortage ? 'stock-short' : ''}`} title={l.shortage ? 'Stock insuffisant' : 'Disponible'}>{l.available}</td>
                          <td className="num">{fmtMoney(l.price_unit, null)}</td>
                          <td className="num">{fmtMoney(l.subtotal, null)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <LinesTable lines={detail.lines} />
                )}
                <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />

                {detail.stock_shortage && (
                  <div className="devis-warn">⚠️ Stock insuffisant pour au moins un article : la livraison fera passer le stock en négatif.</div>
                )}

                <div className="next-step">
                  <span className="next-step-label">Prochaine étape</span>
                  <NextStep d={detail} />
                </div>

                <div className="devis-actions">
                  <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Devis / commande (PDF)</a>
                  {(detail.delivery_status === 'full' || detail.delivery_status === 'partial') && (
                    <a className="devis-odoo" href={`/api/orders/${detail.id}/delivery-pdf`}>⬇ Bon de livraison (PDF)</a>
                  )}
                  {detail.invoice && <a className="devis-odoo" href={`/api/invoices/${detail.invoice.id}/pdf`}>⬇ Facture (PDF)</a>}
                  <a className="devis-odoo" href={detail.invoice ? detail.invoice.web_url : detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
                  <button className="devis-ask" onClick={askForDeal}>💬 Demander une modification</button>
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </>
  );
}

// ---------- Page hôte ----------
const TABS = [
  { key: 'suivi', label: 'Suivi' },
  { key: 'devis', label: 'Devis' },
  { key: 'commandes', label: 'Commandes' },
  { key: 'factures', label: 'Factures' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export default function Ventes() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabKey = TABS.some((t) => t.key === raw) ? (raw as TabKey) : 'suivi';

  function setTab(k: TabKey) {
    setParams(k === 'suivi' ? {} : { tab: k }, { replace: true });
  }

  return (
    <div className="devis-page">
      <div className="ventes-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`ventes-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'suivi' && <SuiviTab />}
      {tab === 'devis' && <DevisTab />}
      {tab === 'commandes' && <CommandesTab />}
      {tab === 'factures' && <FacturesTab />}
    </div>
  );
}
